import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import Logo from "./Logo";

export default function SiteFooter() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [regions, setRegions] = useState(["all"]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const FOOTER_REGIONS = [
    { value: "europe",      label: "Flying from Europe" },
    { value: "asia",        label: "Flying from Asia" },
    { value: "americas",    label: "Flying from the Americas" },
    { value: "africa",      label: "Flying from Africa" },
    { value: "middle_east", label: "Flying from the Middle East" },
    { value: "oceania",     label: "Flying from Oceania" },
  ];

  const toggleRegion = (value) => {
    if (value === "all") {
      setRegions(["all"]);
    } else {
      setRegions((prev) => {
        const filtered = prev.filter((r) => r !== "all" && r !== value);
        const next = prev.includes(value) ? filtered : [...filtered, value];
        return next.length === 0 ? ["all"] : next;
      });
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email,
        first_name: firstName || null,
        regions,
      });
      if (data.status === "already_confirmed") {
        toast.success("ACCESS ALREADY GRANTED.");
      } else if (data.delivery === "sent" || data.delivery === "mock") {
        toast.success("CONFIRM ACCESS — LINK SENT TO YOUR INBOX.");
      } else if (data.delivery === "error" || data.delivery === "rate_limited") {
        toast.success("SUBSCRIBED — Check your email. If no confirmation arrives within 24 hours, try subscribing again.");
      } else {
        toast.success("CHECK YOUR INBOX TO CONFIRM.");
      }
      setEmail("");
      setFirstName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="newsletter" className="mt-24 border-t border-[#2A2A2A] bg-[#0B0B0B] text-white pb-16 md:pb-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 grid md:grid-cols-[1fr_1.1fr] gap-16 items-start">
        <div>
          <div className="mb-8">
            <Logo />
          </div>
          <p className="eyebrow eyebrow-red">Clearance · Level 4</p>
          <h2 className="font-mono text-3xl md:text-4xl mt-4 leading-tight tracking-tight text-white uppercase">
            Deals not on public record.
          </h2>
          <p className="mt-6 text-[#9A8F83] max-w-md leading-relaxed font-sans">
            Mistake fares, hotel flash sales, cruise drops, package deals.
            Declassified one email at a time — only when the price earns it.
          </p>
        </div>

        <div>
          <p className="eyebrow eyebrow-red">// SUBSCRIBE FOR FREE</p>
          <p className="mt-2 text-[#9A8F83] text-[11px] font-mono tracking-[0.12em] uppercase leading-relaxed">
            // WHAT YOU GET: Free email alerts when we spot mistake fares, hotel flash sales, cruise drops, and package deals. No credit card. No catch.
          </p>
          <form onSubmit={subscribe} className="mt-4 grid gap-3" data-testid="newsletter-form">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              className="bg-[#0E0E0E] border border-[#2A2A2A] text-white placeholder-[#888] px-4 py-4 outline-none focus:border-[#CC1A1A] transition-colors font-mono text-sm min-h-[52px]"
              data-testid="newsletter-firstname-input"
              autoComplete="given-name"
            />
            <div className="flex flex-col sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="operator@inbox.com"
                className="flex-1 bg-[#0E0E0E] border border-[#2A2A2A] text-white placeholder-[#888] px-4 py-4 outline-none focus:border-[#CC1A1A] transition-colors font-mono text-sm min-h-[52px]"
                data-testid="newsletter-email-input"
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={loading || !consent}
                className="btn-rd disabled:opacity-60 !rounded-none min-h-[52px]"
                data-testid="newsletter-submit"
              >
                {loading ? "TRANSMITTING…" : "GET FREE DEAL ALERTS →"}
              </button>
            </div>
            {/* Region preferences */}
            <div className="border border-[#2A2A2A] bg-[#111111] px-4 py-3">
              <p className="text-[9px] font-mono tracking-[0.22em] uppercase text-[#9A8F83] mb-2">
                Where are you flying FROM? <span className="text-[#9A8F83] opacity-60">(optional)</span>
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {FOOTER_REGIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={regions.includes(opt.value)}
                      onChange={() => toggleRegion(opt.value)}
                      className="w-3 h-3 shrink-0 accent-[#CC1A1A]"
                    />
                    <span className="text-[10px] font-mono tracking-[0.1em] uppercase text-[#9A8F83]">{opt.label}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer col-span-2 border-t border-[#1E1E1E] pt-1.5 mt-0.5">
                  <input
                    type="checkbox"
                    checked={regions.includes("all")}
                    onChange={() => toggleRegion("all")}
                    className="w-3 h-3 shrink-0 accent-[#CC1A1A]"
                  />
                  <span className="text-[10px] font-mono tracking-[0.1em] uppercase text-[#9A8F83]">Send me all deals <span className="opacity-60">(recommended)</span></span>
                </label>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-1" data-testid="newsletter-consent-label">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-[#CC1A1A]"
                data-testid="newsletter-consent"
              />
              <span className="text-[11px] font-mono tracking-[0.12em] uppercase text-[#9A8F83] leading-relaxed">
                I agree to the{" "}
                <Link to="/privacy" className="text-[#CC1A1A] hover:underline">
                  Privacy Policy
                </Link>{" "}
                and consent to receive deal alerts by email. I confirm I am 16 or older.
              </span>
            </label>
          </form>
          <p className="mt-3 text-[11px] text-[#9A8F83] tracking-[0.14em] font-mono uppercase">
            Free. No spam. Unsubscribe anytime.
          </p>
          <p className="mt-1 text-[11px] text-[#9A8F83] tracking-[0.14em] font-mono uppercase">
            Double opt-in · one-click revocation in every dispatch
          </p>
        </div>
      </div>

      {/* Full-width Channels / Signal row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-10 flex justify-between items-start text-xs font-mono uppercase tracking-widest">
        <div>
          <p className="text-[#CC1A1A]">Channels</p>
          <p className="mt-2">
            <Link to="/?region=Europe" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Europe</Link>
            <span className="text-[#444444]"> · </span>
            <Link to="/?region=Asia" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Asia</Link>
            <span className="text-[#444444]"> · </span>
            <Link to="/?region=Americas" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Americas</Link>
          </p>
          <p>
            <Link to="/?region=Africa" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Africa</Link>
            <span className="text-[#444444]"> · </span>
            <Link to="/?region=Middle+East" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Middle East</Link>
            <span className="text-[#444444]"> · </span>
            <Link to="/?region=Oceania" className="text-[#9A8F83] hover:text-[#CC1A1A] transition-colors">Oceania</Link>
          </p>
        </div>
        <div className="text-right text-[#9A8F83]">
          <p className="text-[#CC1A1A]">Signal</p>
          <p className="mt-2">restricteddeals.com</p>
          <p>encrypted · no tracking</p>
        </div>
      </div>

      <div className="border-t border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 pb-3 flex flex-row items-center justify-between flex-wrap gap-3 text-[11px] font-mono tracking-[0.1em] uppercase text-[#888]">
          <span className="flex items-center flex-wrap gap-x-4 gap-y-1">
            <Link to="/contact" className="text-[#CC1A1A] hover:underline transition-colors">// CONTACT US</Link>
            <Link to="/about" className="text-[#555555] hover:text-[#CC1A1A] transition-colors">About</Link>
            <Link to="/guides" className="text-[#555555] hover:text-[#CC1A1A] transition-colors">Guides</Link>
            <Link to="/privacy" className="text-[#555555] hover:text-[#CC1A1A] transition-colors">Privacy</Link>
            <Link to="/terms" className="text-[#555555] hover:text-[#CC1A1A] transition-colors">Terms</Link>
            <Link to="/cookies" className="text-[#555555] hover:text-[#CC1A1A] transition-colors">Cookies</Link>
          </span>
          <span className="flex items-center gap-3">
            <span className="text-[#CC1A1A]">// FOLLOW</span>
            <a href="https://instagram.com/restricteddeals" target="_blank" rel="noopener noreferrer" className="hover:text-[#CC1A1A] transition-colors">INSTAGRAM</a>
            <span>|</span>
            <a href="https://www.facebook.com/restricteddealsofficial/" target="_blank" rel="noopener noreferrer" className="hover:text-[#CC1A1A] transition-colors">FACEBOOK</a>
            <span>|</span>
            <a href="https://whatsapp.com/channel/0029Vb83Q6f5q08VDiPOKf3m" target="_blank" rel="noopener noreferrer" className="hover:text-[#CC1A1A] transition-colors">WHATSAPP</a>
          </span>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] font-mono tracking-[0.18em] uppercase text-[#888] border-t border-[#1E1E1E] pt-3">
          <span>© {new Date().getFullYear()} RESTRICTED DEALS · ALL INTEL VERIFIED AT BOOKING</span>
          <span>Prices indicative · always check the fare</span>
        </div>
      </div>
    </footer>
  );
}

